// AUPRÉA — Wizard « Nouveau parcours client » — 3 étapes avec autosave
import { useState, useEffect, useRef, useMemo } from "react";
import {
  C, Btn, Input, Textarea, Select, Alert, Spinner, FormField,
  parcoursApi, reglagesApi, DateInput, leadsV2Api, errMsg, BESOIN_TYPE,
} from "./shared";
import { LeadPicker } from "./LeadPicker";
import {
  PARCOURS_CATALOG, getQuestionnaire, computeProgress,
} from "./parcoursQuestionnaires";
import { Preconisations } from "./Preconisations";

// ── Styles utilitaires ──────────────────────────────────────────────────────
const TouchBtn = ({ selected, onClick, children, testid, danger }) => (
  <button onClick={onClick} data-testid={testid}
    style={{
      minHeight: 44, padding: "12px 16px", borderRadius: 10,
      border: selected ? `2px solid ${danger ? C.red : C.gold}` : `1px solid ${C.border}`,
      background: selected ? (danger ? C.redlt : C.goldlt) : "#fff",
      color: selected ? (danger ? C.red : C.golddm) : C.text,
      fontSize: 13, fontWeight: selected ? 700 : 500, cursor: "pointer",
      fontFamily: "inherit", textAlign: "left", width: "100%",
      transition: "all .15s",
    }}>{children}</button>
);

const Breadcrumb = ({ step }) => (
  <div style={{ display: "flex", gap: 4, marginBottom: 24, flexWrap: "wrap" }}>
    {["1. Client & parcours", "2. Questionnaire", "3. Préconisations & proposition"].map((label, i) => {
      const s = i + 1;
      const active = s === step;
      const done = s < step;
      return (
        <div key={s} style={{ flex: 1, minWidth: 200,
          padding: "12px 16px", borderRadius: 10,
          background: active ? C.navy : (done ? C.teallt : "#fff"),
          color: active ? "#fff" : (done ? C.teal : C.mid),
          border: `.5px solid ${active ? C.navy : (done ? C.teal : C.border)}`,
          fontSize: 13, fontWeight: active ? 700 : 500,
          display: "flex", alignItems: "center", gap: 8,
        }}>
          <span style={{
            width: 22, height: 22, borderRadius: 11, display: "grid", placeItems: "center",
            background: active ? C.gold : (done ? C.teal : C.gray2),
            color: active || done ? "#fff" : C.muted, fontSize: 11, fontWeight: 700,
          }}>{done ? "✓" : s}</span>
          {label.replace(/^\d+\.\s*/, "")}
        </div>
      );
    })}
  </div>
);

const ProgressBar = ({ pct, label }) => (
  <div style={{ marginBottom: 16 }}>
    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: C.mid, marginBottom: 6 }}>
      <span>{label}</span><span style={{ fontWeight: 700, color: C.navy }}>{pct}%</span>
    </div>
    <div style={{ height: 6, borderRadius: 3, background: C.gray2, overflow: "hidden" }}>
      <div style={{ width: `${pct}%`, height: "100%", background: C.gold, transition: "width .3s" }} />
    </div>
  </div>
);

const SaveIndicator = ({ state }) => {
  const cfg = { idle: null, saving: { c: C.mid, t: "Enregistrement…" }, saved: { c: C.green, t: "✓ Brouillon enregistré" }, error: { c: C.red, t: "⚠ Erreur d'enregistrement" } }[state];
  if (!cfg) return null;
  return <span style={{ fontSize: 11, color: cfg.c, fontWeight: 500 }}>{cfg.t}</span>;
};

// ═══════════════ ÉTAPE 1 — FICHE + CHOIX PARCOURS ════════════════════════════
const Etape1 = ({ parcours, fiche, setFiche, typeParcours, setTypeParcours, reglages, lead, onPickLead, source, setSource }) => {
  const rgpdTxt = reglages?.textes?.rgpd || "";
  const set = (k, v) => setFiche({ ...fiche, [k]: v });
  const cpValid = /^\d{5}$/.test(fiche.code_postal || "");
  return (
    <>
      <div style={{ background: "#fff", border: `.5px solid ${C.border}`, borderRadius: 12, padding: 24, marginBottom: 20 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: C.navy, marginBottom: 4, fontFamily: "'Playfair Display', serif" }}>Fiche client</div>
        <div style={{ fontSize: 12, color: C.mid, marginBottom: 20 }}>Champs obligatoires sauf mention contraire.</div>
        <LeadPicker lead={lead} onPick={onPickLead} source={source} onSource={setSource} locked={!!parcours?.leadId} />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <FormField label="Nom" required><Input value={fiche.nom} onChange={(e) => set("nom", e.target.value)} data-testid="p1-nom" /></FormField>
          <FormField label="Prénom" required><Input value={fiche.prenom} onChange={(e) => set("prenom", e.target.value)} data-testid="p1-prenom" /></FormField>
          <FormField label="E-mail" required><Input type="email" value={fiche.email} onChange={(e) => set("email", e.target.value)} data-testid="p1-email" /></FormField>
          <FormField label="Téléphone (obligatoire si envoi WhatsApp / SMS)">
            <Input value={fiche.telephone} onChange={(e) => set("telephone", e.target.value)} placeholder="+33 6 12 34 56 78" data-testid="p1-tel" />
          </FormField>
          <FormField label="Date de naissance" required><DateInput value={fiche.date_naissance} onChange={(v) => set("date_naissance", v)} data-testid="p1-dob" /></FormField>
          <FormField label="Code postal de résidence" required error={fiche.code_postal && !cpValid ? "5 chiffres attendus" : ""}>
            <Input maxLength={5} value={fiche.code_postal} onChange={(e) => set("code_postal", e.target.value.replace(/\D/g, "").slice(0, 5))} data-testid="p1-cp" />
          </FormField>
          <FormField label="Date du rendez-vous" required><DateInput value={fiche.date_rdv} onChange={(v) => set("date_rdv", v)} data-testid="p1-rdv" /></FormField>
        </div>
        <label style={{ display: "flex", gap: 10, marginTop: 16, cursor: "pointer", alignItems: "start" }}>
          <input type="checkbox" checked={!!fiche.rgpd_accept} onChange={(e) => setFiche({ ...fiche, rgpd_accept: e.target.checked, rgpd_texte: e.target.checked ? rgpdTxt : "", rgpd_accept_at: e.target.checked ? new Date().toISOString() : "" })} style={{ marginTop: 3, width: 16, height: 16 }} data-testid="p1-rgpd" />
          <span style={{ fontSize: 12, color: C.mid, lineHeight: 1.5 }}>
            <strong style={{ color: C.text }}>Consentement RGPD (requis)</strong><br />
            {rgpdTxt}
          </span>
        </label>
      </div>

      <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: ".1em", marginBottom: 12 }}>Choix du parcours</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 14 }}>
        {PARCOURS_CATALOG.map(p => {
          const active = typeParcours === p.code;
          return (
            <button key={p.code} onClick={() => setTypeParcours(p.code)}
              data-testid={`p1-parcours-${p.code}`}
              style={{
                cursor: "pointer", background: "#fff",
                border: `2px solid ${active ? p.couleur : C.border}`, borderRadius: 14, padding: 20,
                textAlign: "left", fontFamily: "inherit", minHeight: 180,
                display: "flex", flexDirection: "column", gap: 8, position: "relative",
                boxShadow: active ? `0 4px 20px ${p.couleur}22` : "none",
                transition: "all .15s",
              }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{ width: 12, height: 12, borderRadius: 6, background: p.couleur }} />
                <span style={{ fontSize: 10, color: p.couleur, fontWeight: 700, letterSpacing: ".07em", textTransform: "uppercase" }}>{p.cible}</span>
              </div>
              <div style={{ fontSize: 16, fontWeight: 700, color: C.navy, fontFamily: "'Playfair Display', serif" }}>{p.titre}</div>
              <div style={{ fontSize: 12, color: C.mid, flex: 1 }}>{p.intro}</div>
              <div style={{ fontSize: 11, fontWeight: 600, color: p.couleur, marginTop: 4 }}>{p.tarif}</div>
              {active && <div style={{ position: "absolute", top: 12, right: 12, width: 24, height: 24, borderRadius: 12, background: p.couleur, color: "#fff", display: "grid", placeItems: "center", fontSize: 12, fontWeight: 700 }}>✓</div>}
            </button>
          );
        })}
      </div>
    </>
  );
};

// ═══════════════ ÉTAPE 2 — QUESTIONNAIRE DYNAMIQUE ═══════════════════════════
const RenderQuestion = ({ q, value, onChange, testidPrefix }) => {
  if (q.type === "opn" || q.type === "gradue3" || q.type === "gradue3+nc") {
    return (
      <div style={{ display: "grid", gridTemplateColumns: q.type === "opn" ? "repeat(3, 1fr)" : "1fr", gap: 8 }}>
        {q.options.map(opt => (
          <TouchBtn key={opt.value} selected={value === opt.value} onClick={() => onChange(opt.value)} testid={`${testidPrefix}-${opt.value}`}>
            {opt.label}
          </TouchBtn>
        ))}
      </div>
    );
  }
  if (q.type === "oui_non") {
    return (
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        {[{ v: "oui", l: "Oui" }, { v: "non", l: "Non" }].map(o => (
          <TouchBtn key={o.v} selected={value === o.v} onClick={() => onChange(o.v)} testid={`${testidPrefix}-${o.v}`}>{o.l}</TouchBtn>
        ))}
      </div>
    );
  }
  return null;
};

const RenderChamp = ({ champ, value, onChange, testidPrefix }) => {
  if (champ.type === "select") {
    return (
      <Select value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testidPrefix}>
        <option value="">Sélectionner…</option>
        {champ.options.map(o => <option key={o} value={o}>{o}</option>)}
      </Select>
    );
  }
  if (champ.type === "date")   return <Input type="date"   value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testidPrefix} />;
  if (champ.type === "number") return <Input type="number" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testidPrefix} />;
  if (champ.type === "annee")  return <Input type="number" min={champ.min || 1900} max={new Date().getFullYear()} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testidPrefix} />;
  return <Input value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testidPrefix} />;
};

// Arbre généalogique — sous-composant réutilisable
const ArbreGenea = ({ arbre, setArbre, schema }) => {
  const add = () => setArbre([...(arbre || []), Object.fromEntries(schema.map(s => [s.id, ""]))]);
  const del = (i) => setArbre(arbre.filter((_, j) => j !== i));
  const set = (i, k, v) => setArbre(arbre.map((row, j) => j === i ? { ...row, [k]: v } : row));
  return (
    <div>
      {(arbre || []).map((row, i) => (
        <div key={i} style={{ display: "grid", gridTemplateColumns: `repeat(${schema.length}, 1fr) auto`, gap: 8, marginBottom: 8, alignItems: "end" }}>
          {schema.map(s => (
            <FormField key={s.id} label={s.label} compact>
              <RenderChamp champ={s} value={row[s.id]} onChange={(v) => set(i, s.id, v)} testidPrefix={`arbre-${i}-${s.id}`} />
            </FormField>
          ))}
          <Btn small variant="red" onClick={() => del(i)} data-testid={`arbre-del-${i}`}>×</Btn>
        </div>
      ))}
      <Btn small onClick={add} data-testid="arbre-add">+ Ajouter une personne</Btn>
    </div>
  );
};

const Etape2 = ({ typeParcours, parcours, updateReponses, updateComp, updateSpec }) => {
  const q = getQuestionnaire(typeParcours);
  const rep = parcours?.reponses || {};
  const comp = parcours?.reponses_complementaires || {};
  const spec = parcours?.donnees_specifiques || {};
  if (!q) return null;

  return (
    <>
      {/* Champs société pour dirigeants — en tête */}
      {q.societe && (
        <div style={{ background: "#fff", border: `.5px solid ${C.border}`, borderRadius: 12, padding: 20, marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.navy, marginBottom: 12, fontFamily: "'Playfair Display', serif" }}>{q.societe.titre}</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            {q.societe.champs.map(c => (
              <FormField key={c.id} label={c.label} required={c.required}>
                <RenderChamp champ={c} value={spec.societe?.[c.id]} onChange={(v) => updateSpec({ societe: { ...(spec.societe || {}), [c.id]: v }})} testidPrefix={`spec-societe-${c.id}`} />
              </FormField>
            ))}
          </div>
        </div>
      )}

      {/* Sections questions ou champs */}
      {q.sections.map(sec => (
        <div key={sec.code} style={{ background: "#fff", border: `.5px solid ${C.border}`, borderRadius: 12, padding: 20, marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.navy, marginBottom: 16, fontFamily: "'Playfair Display', serif" }}>{sec.titre}</div>

          {(sec.questions || []).map((qq, idx) => (
            <div key={qq.id} style={{ marginBottom: 22, paddingBottom: 16, borderBottom: idx < sec.questions.length - 1 ? `1px dashed ${C.border}` : "none" }}>
              <div style={{ fontSize: 13, color: C.text, marginBottom: 10, lineHeight: 1.5, fontWeight: 500 }}>
                <span style={{ color: C.muted, fontWeight: 700, marginRight: 6 }}>{qq.id.toUpperCase()}</span>
                {qq.texte}
              </div>
              <RenderQuestion q={qq} value={rep[qq.id]} onChange={(v) => updateReponses({ [qq.id]: v })} testidPrefix={`q-${qq.id}`} />
            </div>
          ))}

          {(sec.champs || []).map((c, idx) => (
            <div key={c.id} style={{ marginBottom: 14, paddingBottom: 10, borderBottom: idx < sec.champs.length - 1 ? `1px dashed ${C.border}` : "none" }}>
              <FormField label={c.label} required={c.required}>
                <RenderChamp champ={c} value={spec[sec.code]?.[c.id]} onChange={(v) => updateSpec({ [sec.code]: { ...(spec[sec.code] || {}), [c.id]: v }})} testidPrefix={`spec-${sec.code}-${c.id}`} />
              </FormField>
            </div>
          ))}
        </div>
      ))}

      {/* Arbre généalogique (ouvrir_succession) */}
      {q.arbre_genealogique && (
        <div style={{ background: "#fff", border: `.5px solid ${C.border}`, borderRadius: 12, padding: 20, marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.navy, marginBottom: 4, fontFamily: "'Playfair Display', serif" }}>{q.arbre_genealogique.titre}</div>
          <div style={{ fontSize: 11, color: C.mid, marginBottom: 12 }}>{q.arbre_genealogique.aide}</div>
          <ArbreGenea arbre={spec.arbre || []} setArbre={(a) => updateSpec({ arbre: a })} schema={q.arbre_genealogique.schema} />
        </div>
      )}

      {/* Documents (ouvrir_succession : possession/à obtenir) */}
      {q.documents && (
        <div style={{ background: "#fff", border: `.5px solid ${C.border}`, borderRadius: 12, padding: 20, marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.navy, marginBottom: 12, fontFamily: "'Playfair Display', serif" }}>{q.documents.titre}</div>
          {q.documents.items.map(item => (
            <div key={item.id} style={{ marginBottom: 12, display: "grid", gridTemplateColumns: "1fr auto", alignItems: "center", gap: 12 }}>
              <div style={{ fontSize: 13, color: C.text }}>{item.label}</div>
              <div style={{ display: "flex", gap: 6 }}>
                {q.documents.options.map(opt => (
                  <TouchBtn key={opt} selected={spec.docs?.[item.id] === opt} onClick={() => updateSpec({ docs: { ...(spec.docs || {}), [item.id]: opt }})} testid={`doc-${item.id}-${opt.slice(0,3)}`}>
                    <span style={{ fontSize: 11 }}>{opt}</span>
                  </TouchBtn>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Documents multi-select (foncier_clair) */}
      {q.documents_multi && (
        <div style={{ background: "#fff", border: `.5px solid ${C.border}`, borderRadius: 12, padding: 20, marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.navy, marginBottom: 12, fontFamily: "'Playfair Display', serif" }}>{q.documents_multi.titre}</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            {q.documents_multi.options.map(opt => {
              const list = spec[q.documents_multi.id] || [];
              const checked = list.includes(opt);
              return (
                <label key={opt} style={{ display: "flex", gap: 8, alignItems: "center", padding: 10, border: `.5px solid ${checked ? C.gold : C.border}`, borderRadius: 8, background: checked ? C.goldlt : "#fff", cursor: "pointer" }}>
                  <input type="checkbox" checked={checked} onChange={() => {
                    const next = checked ? list.filter(o => o !== opt) : [...list, opt];
                    updateSpec({ [q.documents_multi.id]: next });
                  }} data-testid={`docmulti-${opt.slice(0, 8)}`} />
                  <span style={{ fontSize: 12 }}>{opt}</span>
                </label>
              );
            })}
          </div>
        </div>
      )}

      {/* Bloc complémentaire — visible uniquement par vous */}
      {q.complementaires && (
        <div style={{ background: C.goldlt, border: `2px solid ${C.gold}`, borderRadius: 12, padding: 20, marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.golddm, marginBottom: 4, fontFamily: "'Playfair Display', serif" }}>{q.complementaires.titre}</div>
          <div style={{ fontSize: 11, color: C.golddm, marginBottom: 16 }}>Ces réponses restent internes et ne seront jamais montrées au client.</div>
          {q.complementaires.questions.map((qq, idx) => (
            <div key={qq.id} style={{ marginBottom: 14, paddingBottom: 10, borderBottom: idx < q.complementaires.questions.length - 1 ? `1px dashed ${C.gold}44` : "none" }}>
              <div style={{ fontSize: 13, color: C.text, marginBottom: 8, fontWeight: 500 }}>{qq.texte}</div>
              {qq.type === "oui_non" ? (
                <RenderQuestion q={qq} value={comp[qq.id]} onChange={(v) => updateComp({ [qq.id]: v })} testidPrefix={`comp-${qq.id}`} />
              ) : (
                <RenderChamp champ={qq} value={comp[qq.id]} onChange={(v) => updateComp({ [qq.id]: v })} testidPrefix={`comp-${qq.id}`} />
              )}
            </div>
          ))}
        </div>
      )}
    </>
  );
};

// ═══════════════ WIZARD PRINCIPAL ════════════════════════════════════════════
export const ParcoursClient = ({ user, initialParcoursId, onClose, onFinished }) => {
  const [parcours, setParcours] = useState(null);   // objet complet
  const [step, setStep] = useState(1);
  const [saveState, setSaveState] = useState("idle");
  const [reglages, setReglages] = useState(null);
  const [errMsg, setErrMsg] = useState("");

  // État local pilote (édité par l'utilisateur → autosave debounced)
  const [fiche, setFiche] = useState({ nom: "", prenom: "", email: "", telephone: "", date_naissance: "", code_postal: "", date_rdv: "", rgpd_accept: false });
  const [typeParcours, setTypeParcours] = useState("");
  const [lead, setLead] = useState(null);
  const [source, setSource] = useState("");
  const [linking, setLinking] = useState(false);
  const pickLead = (l) => {
    setLead(l);
    if (!l) return;
    const i = l.identite || {};
    setFiche(f => ({ ...f, prenom: i.prenom || "", nom: i.nom || "", email: i.email || "", telephone: i.telephone || "" }));
    if (!typeParcours && BESOIN_TYPE[l.besoin]) setTypeParcours(BESOIN_TYPE[l.besoin]);
  };

  const saveTimer = useRef(null);
  const isMounted = useRef(true);

  // Charge réglages + éventuel parcours existant
  useEffect(() => {
    isMounted.current = true;
    reglagesApi.consultant().then(setReglages).catch(() => {});
    if (initialParcoursId) {
      parcoursApi.get(initialParcoursId).then(p => {
        if (!isMounted.current) return;
        setParcours(p);
        setFiche(p.fiche || fiche);
        setTypeParcours(p.type_parcours || "");
        if (p.leadId) leadsV2Api.get(p.leadId).then(setLead).catch(() => {});
        // Reste à l'étape 1 tant que la fiche n'est pas complète (utile en reprise de brouillon
        // et lors d'un « Démarrer le bilan » depuis un RDV où CP/date_naissance/RGPD manquent).
        const f = p.fiche || {};
        const ficheComplete = p.type_parcours && f.nom && f.prenom && f.email && f.date_naissance
          && /^\d{5}$/.test(f.code_postal || "") && f.date_rdv && f.rgpd_accept;
        const hasFormules = p.formules && Object.keys(p.formules).length > 0;
        setStep(!ficheComplete ? 1 : (hasFormules || p.statut === "realise" ? 3 : 2));
      }).catch((e) => setErrMsg(e?.response?.data?.detail || "Impossible de charger le brouillon"));
    } else {
      // Pré-sélection du type depuis le Catalogue (bouton « Lancer un parcours »)
      const pl = sessionStorage.getItem("auprea_lead_bilan");
      if (pl) { sessionStorage.removeItem("auprea_lead_bilan"); pickLead(JSON.parse(pl)); }
      const pre = sessionStorage.getItem("auprea_new_parcours_type");
      if (pre) {
        setTypeParcours(pre);
        sessionStorage.removeItem("auprea_new_parcours_type");
      }
    }
    return () => { isMounted.current = false; };
    // eslint-disable-next-line
  }, [initialParcoursId]);

  // Autosave debounced 500ms sur fiche+type
  useEffect(() => {
    if (!typeParcours) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      setSaveState("saving");
      try {
        if (!parcours) {
          const created = await parcoursApi.create({
            conseillerId: user.conseiller_id || user.id,
            type_parcours: typeParcours,
            fiche,
            reponses: {}, reponses_complementaires: {}, donnees_specifiques: {},
            statut: "brouillon", leadId: lead?.id || null,
          });
          setParcours(created);
        } else {
          const upd = await parcoursApi.patch(parcours.id, { fiche, ...(parcours.type_parcours !== typeParcours ? { /* type change → recréer? on ignore */ } : {}) });
          setParcours(upd);
        }
        setSaveState("saved");
        setTimeout(() => isMounted.current && setSaveState("idle"), 2000);
      } catch (e) {
        setSaveState("error");
        setErrMsg(e?.response?.data?.detail || "Erreur d'enregistrement");
      }
    }, 500);
    return () => saveTimer.current && clearTimeout(saveTimer.current);
    // eslint-disable-next-line
  }, [fiche, typeParcours]);

  // Autosave immédiat pour un patch réponses/complémentaires/spéc
  const patchNow = async (patch) => {
    if (!parcours) return;
    setSaveState("saving");
    try {
      const upd = await parcoursApi.patch(parcours.id, patch);
      setParcours(upd);
      setSaveState("saved");
      setTimeout(() => isMounted.current && setSaveState("idle"), 1500);
    } catch (e) {
      setSaveState("error");
      setErrMsg(e?.response?.data?.detail || "Erreur d'enregistrement");
    }
  };

  const updateReponses = (patch) => {
    setParcours(prev => prev ? { ...prev, reponses: { ...prev.reponses, ...patch } } : prev);
    patchNow({ reponses: { ...(parcours?.reponses || {}), ...patch } });
  };
  const updateComp = (patch) => {
    setParcours(prev => prev ? { ...prev, reponses_complementaires: { ...prev.reponses_complementaires, ...patch } } : prev);
    patchNow({ reponses_complementaires: { ...(parcours?.reponses_complementaires || {}), ...patch } });
  };
  const updateSpec = (patch) => {
    setParcours(prev => prev ? { ...prev, donnees_specifiques: { ...prev.donnees_specifiques, ...patch } } : prev);
    patchNow({ donnees_specifiques: { ...(parcours?.donnees_specifiques || {}), ...patch } });
  };

  // Validation étape 1
  const step1Complete = useMemo(() => {
    return fiche.nom && fiche.prenom && fiche.email && fiche.date_naissance
      && /^\d{5}$/.test(fiche.code_postal || "") && fiche.date_rdv && fiche.rgpd_accept && typeParcours && (lead || source || parcours?.leadId);
  }, [fiche, typeParcours, lead, source, parcours]);

  const continuer = async () => {
    if (!parcours) return;
    setLinking(true); setErrMsg("");
    try {
      if (!parcours.leadId) {
        await parcoursApi.patch(parcours.id, { fiche });
        const r = await leadsV2Api.lierParcours(parcours.id, lead ? { leadId: lead.id } : { source });
        setParcours({ ...parcours, leadId: r.leadId });
      }
      setStep(2);
    } catch (e) { setErrMsg(errMsg(e)); }
    finally { setLinking(false); }
  };

  // Validation étape 2
  const progress = useMemo(() => {
    if (!typeParcours) return { total: 0, done: 0, remaining: 0, pct: 0 };
    return computeProgress(getQuestionnaire(typeParcours), parcours);
  }, [typeParcours, parcours]);

  const finaliserBilan = async () => {
    if (progress.remaining > 0) return;
    // Ne finalise PAS ici : on passe à l'étape 3 (préconisations) qui gère la finalisation
    setStep(3);
  };

  const typeInfo = PARCOURS_CATALOG.find(p => p.code === typeParcours);

  return (
    <div style={{ maxWidth: 1200, margin: "0 auto" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 700, color: C.navy, fontFamily: "'Playfair Display', serif" }}>Nouveau parcours client</div>
          {typeInfo && <div style={{ fontSize: 12, color: typeInfo.couleur, fontWeight: 600, marginTop: 4 }}>{typeInfo.titre} · {typeInfo.cible}</div>}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <SaveIndicator state={saveState} />
          <Btn onClick={onClose} data-testid="parcours-close">Fermer</Btn>
        </div>
      </div>

      {errMsg && <Alert type="error"><span>⚠</span><span>{errMsg}</span></Alert>}
      <Breadcrumb step={step} />

      {step === 1 && (
        <>
          <Etape1 parcours={parcours} fiche={fiche} setFiche={setFiche} typeParcours={typeParcours} setTypeParcours={setTypeParcours} reglages={reglages} lead={lead} onPickLead={pickLead} source={source} setSource={setSource} />
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 24 }}>
            <Btn variant="gold" disabled={!step1Complete || linking || !parcours} onClick={continuer} data-testid="parcours-goto-2">
              Continuer vers le questionnaire →
            </Btn>
          </div>
        </>
      )}

      {step === 2 && (
        <>
          <ProgressBar pct={progress.pct} label={`Progression questionnaire : ${progress.done}/${progress.total} réponses`} />
          <Etape2 typeParcours={typeParcours} parcours={parcours} updateReponses={updateReponses} updateComp={updateComp} updateSpec={updateSpec} />
          <div style={{ display: "flex", justifyContent: "space-between", gap: 10, marginTop: 24, flexWrap: "wrap" }}>
            <Btn onClick={() => setStep(1)} data-testid="parcours-back-1">← Retour fiche client</Btn>
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              {progress.remaining > 0 && <span style={{ fontSize: 12, color: C.amber, fontWeight: 500 }}>Il reste {progress.remaining} réponse(s) à renseigner</span>}
              <Btn variant="gold" disabled={progress.remaining > 0} onClick={finaliserBilan} data-testid="parcours-finaliser">Voir les préconisations →</Btn>
            </div>
          </div>
        </>
      )}

      {step === 3 && parcours && (
        <Preconisations
          parcours={parcours}
          onPatch={(upd) => setParcours(upd)}
          onFinished={() => onFinished && onFinished(parcours.id)}
        />
      )}
    </div>
  );
};
